import { context, trace, type Span } from '@opentelemetry/api'
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosRequestHeaders } from 'axios'

import { showToast } from '../../utils/toastMessage'

interface RequestMetadata {
  span?: Span
  _otelContext?: ReturnType<typeof context.active>
}

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata
  }
}

type TenantBase = 'management'
type TenantType = 'service' | 'food'
type CommonBase = 'common'

type TenantResource =
  | 'bookings'
  | 'booking'
  | 'booking/series'
  | 'customer'
  | 'customers'
  | 'calendar/events'
  | 'calendar/resources'
  | 'settings'
  | 'tenant'
  | 'apiKeys'
  | 'employee/absence'
  | 'employees'
  | 'send-mail'
  | 'utilization'
  | 'event'
  | 'utilization/tables'

type TenantDynamicResource =
  | `booking/${string}`
  | `booking/${string}/${string}`
  | `customer/${string}`
  | `apiKeys/${string}`
  | `employees/${string}`

type CommonResource =
  | 'user'
  | 'user/settings'
  | 'customer'
  | 'customer/booking-counts'
  | 'api-key'
  | 'calendar/resources'
  | 'changelog'
  | 'mail'
  | 'mail-config'
  | 'mail-config/provider'
  | 'mail-config/test'
  | 'settings'
  | 'tenant'
  | 'daily-notes'
  | 'rooms'
  | 'employees'
  | 'services'
  | 'shifts'

type CommonDynamicResource =
  | `changelog/${string}`
  | `customer/${string}/bookings`
  | `customer/${string}`
  | `api-key/${string}`
  | `daily-notes/${string}`
  | `employees/${string}`
  | `rooms/${string}`
  | `services/${string}`
  | `shifts/${string}`

type TenantEndpoint = `${TenantType}/${TenantBase}/${TenantResource}`
type TenantDynamicEndpoint = `${TenantType}/${TenantBase}/${TenantDynamicResource}`
type CommonEndpoint = `${CommonBase}/${TenantBase}/${CommonResource}`
type CommonDynamicEndpoint = `${CommonBase}/${TenantBase}/${CommonDynamicResource}`

abstract class ApiService {
  private tenantId: string
  private token: string
  protected tenantType?: string

  private readonly baseAxios: AxiosInstance

  constructor(token: string, tenantId?: string, tenantType?: string) {
    const { VITE_APP_API_URL } = import.meta.env
    this.tenantId = tenantId ?? ''
    this.token = token
    this.tenantType = tenantType
    this.baseAxios = axios.create({
      baseURL: VITE_APP_API_URL,
      timeout: 30000, // Increased from 3000ms to 30000ms (30 seconds)
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        tenantId: this.tenantId,
        Authorization: `Bearer ${this.token}`,
      },
    })

    const generateRandomHex = (length: number) =>
      Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('')

    this.baseAxios.interceptors.request.use((config) => {
      const tracer = trace.getTracer('api-service')
      const span = tracer.startSpan(`${config.method?.toUpperCase()} ${config.url ?? ''}`)

      if (!config.metadata) {
        config.metadata = {}
      }
      config.metadata.span = span

      const currentContext = trace.setSpan(context.active(), span)
      config.metadata._otelContext = currentContext

      const { traceId, spanId, traceFlags } = span.spanContext()
      const safeTraceId =
        traceId && traceId !== '00000000000000000000000000000000' ? traceId : generateRandomHex(32)
      const safeSpanId = spanId && spanId !== '0000000000000000' ? spanId : generateRandomHex(16)
      const safeTraceFlags = (traceFlags || 1).toString(16).padStart(2, '0')

      const traceparent = `00-${safeTraceId}-${safeSpanId}-${safeTraceFlags}`
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders
      }
      ;(config.headers as Record<string, string>)['traceparent'] = traceparent

      return context.with(currentContext, () => config)
    })

    this.baseAxios.interceptors.response.use(
      (response) => {
        const span = response.config.metadata?.span as Span | undefined
        if (span) {
          span.end()
        }
        return response
      },
      (error) => {
        const span = error.config?.metadata?.span as Span | undefined
        if (span) {
          span.recordException(error)
          span.end()
        }
        if (
          error.response?.status === 403 &&
          error.response?.data?.code === 'INSUFFICIENT_PERMISSIONS'
        ) {
          showToast({
            severity: 'error',
            detail: error.response?.data?.error ?? 'Du hast keine Berechtigung für diese Aktion.',
          })
        }
        throw error
      }
    )
  }

  protected post = async <TRequest = unknown, TResponse = unknown>(
    url: TenantEndpoint | TenantDynamicEndpoint | CommonEndpoint | CommonDynamicEndpoint,
    data: TRequest,
    config?: AxiosRequestConfig<TRequest>
  ) => await this.baseAxios.post<TResponse>(url, data, config)

  protected put = async <TRequest = unknown, TResponse = unknown>(
    url: TenantEndpoint | TenantDynamicEndpoint | CommonEndpoint | CommonDynamicEndpoint,
    data?: TRequest,
    config?: AxiosRequestConfig<TRequest>
  ) => await this.baseAxios.put<TResponse>(url, data, config)

  protected get = async <TResponse = unknown>(
    url: TenantEndpoint | TenantDynamicEndpoint | CommonEndpoint | CommonDynamicEndpoint,
    config?: AxiosRequestConfig
  ) => await this.baseAxios.get<TResponse>(url, config)

  protected delete = async <TResponse = void>(
    url: TenantEndpoint | TenantDynamicEndpoint | CommonEndpoint | CommonDynamicEndpoint,
    config?: AxiosRequestConfig
  ) => await this.baseAxios.delete<TResponse>(url, config)

  protected patch = async <TRequest = unknown, TResponse = unknown>(
    url: TenantEndpoint | TenantDynamicEndpoint | CommonEndpoint | CommonDynamicEndpoint,
    data: Partial<TRequest>,
    config?: AxiosRequestConfig<TRequest>
  ) => await this.baseAxios.patch<TResponse>(url, data, config)

  /**
   * Build URL for tenant-specific endpoints with type safety
   * Axios automatically handles baseURL concatenation and slash deduplication
   */
  protected buildTenantUrl<T extends TenantResource | TenantDynamicResource>(
    resource: T
  ): TenantEndpoint | TenantDynamicEndpoint {
    const type = this.tenantType
    if (!type) {
      throw new Error('TenantType is required for tenant-specific URLs')
    }
    return `${type.toLowerCase()}/management/${resource}` as TenantEndpoint | TenantDynamicEndpoint
  }

  /**
   * Build URL for common endpoints with type safety
   */
  protected buildCommonUrl<T extends CommonResource | CommonDynamicResource>(
    resource: T
  ): CommonEndpoint | CommonDynamicEndpoint {
    return `common/management/${resource}` as CommonEndpoint | CommonDynamicEndpoint
  }
}

export default ApiService
